import React from "react";

/**
 * Donut gauge with accessible progress semantics.
 * Props:
 * - percent (0..100)
 * - size (px) -> default 140
 * - stroke (px) -> default 12
 * - label?: string (defaults to "Overall Progress")
 * - subtitle?: string
 */
export default function ProgressGauge({
  percent = 0,
  size = 140,
  stroke = 12,
  label = "Overall Progress",
  subtitle,
}) {
  const clamped = Math.max(0, Math.min(100, percent));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (clamped / 100) * c;

  return (
    <figure
      className="flex flex-col items-center justify-center"
      role="group"
      aria-label={label}
    >
      <svg
        width={size}
        height={size}
        className="block"
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        {/* track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="#E5E7EB"
          strokeWidth={stroke}
          fill="none"
        />
        {/* subtle tick arc background (gives depth) */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="url(#gaugeTrackGrad)"
          strokeWidth={stroke}
          fill="none"
          opacity="0.25"
        />
        {/* progress */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="currentColor"
          className="text-teal-500"
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        {/* center label */}
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-gray-900 font-semibold"
          style={{ fontSize: Math.max(14, size * 0.14) }}
        >
          {clamped}%
        </text>
        {/* defs for subtle gradient */}
        <defs>
          <linearGradient id="gaugeTrackGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#000" stopOpacity="0.0" />
            <stop offset="100%" stopColor="#000" stopOpacity="0.05" />
          </linearGradient>
        </defs>
      </svg>
      <figcaption className="text-sm text-gray-500 mt-2">
        {subtitle || label}
      </figcaption>
    </figure>
  );
}
